import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import { forwardRef } from "react";

import { cn } from "../../../lib";

const buttonVariants = cva(
  "inline-flex items-center gap-x-2 rounded-md text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lf-blue-dark disabled:opacity-50 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-lf-red-dark shadow hover:bg-lf-red-dark/90 text-white",
        outline:
          "border border-lf-red-dark bg-background shadow-sm hover:bg-gray-50 text-lf-red-dark",
        secondary:
          "bg-lf-blue-dark text-lf-blue-dark-foreground shadow-sm hover:bg-lf-blue-dark/80 text-white",
        ghost:
          "hover:bg-lf-red-dark hover:text-lf-red-dark-foreground hover:text-white",
        light: "hover:bg-lf-gray hover:text-black aspect-square",
        link: "underline-offset-4 hover:underline",
        none: "border-opacity-0 shadow-none",
      },
      size: {
        default: "px-6 py-2",
        sm: "rounded-md px-3 text-xs",
        lg: "rounded-md px-8",
        xlg: "rounded-md px-8 py-2",
        icon: "w-12 m-2 text-lg aspect-square shadow-none",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { variant = "default", size = "default", className, ...rest } = props;
  return (
    <button
      className={cn(buttonVariants({ variant, size }), className)}
      ref={ref}
      {...rest}
    />
  );
});

Button.displayName = "Button";

export default Button;
export { buttonVariants };
export type { ButtonProps };
